import { Link } from 'react-router-dom';

export default function Tides() {
  return (
    <section className="as-section">
      <div className="container">
        <h1 className="as-page-title">Tides</h1>
        <p className="as-lead">
          This module guides you through how tides work and how to carry out tidal
          calculations using the Tide Tables for the purposes of achieving AMSA Master
          qualifications. This module uses the 2025 Queensland Tide Table as a reference
          for exercises, references and notes acknowledging the copyright, acknowledgements
          and disclaimers found on the inside cover of the MSQ Queensland Tide Tables.
        </p>

        <div className="as-callout">
          <strong>What you will learn in this module</strong>
          <p className="mb-0">
            Understanding tidal definitions, reading standard port tables, calculating
            tides at secondary ports and applying UKC calculations.
          </p>
        </div>

        <h2 className="as-subheading">Introduction</h2>
        <p>
          Tides are the periodic rise and fall of water levels in oceans, seas, bays, and
          other bodies of water, primarily caused by the gravitational pull of the moon
          and, to a lesser extent, the sun. This gravitational interaction creates a bulge
          of water on both the side of the Earth facing the moon and the opposite side,
          leading to high tides at these locations. As the Earth rotates, different areas
          experience high and low tides as they pass through these tidal bulges.
        </p>
        <p>
          When the sun, Earth, and moon align (during full and new moon phases), the
          gravitational forces combine, leading to higher high tides and lower low tides,
          known as spring tides. When the sun and moon are at right angles to each other
          (during the first and third quarter phases), the gravitational forces partially
          cancel each other out, resulting in smaller tidal ranges, called neap tides.
        </p>
        <p>
          Tide tables can be used for any given locale to find the predicted times and
          tidal range. They are however only predictions, the actual time and height of
          the tide is affected by wind and atmospheric pressure. Most coastal areas
          experience semi-diurnal tides which are two high tides and two low tides in a
          day, due to the moon&rsquo;s orbital period and the Earth&rsquo;s rotation. Other
          locations have diurnal tides being one high and low tide each day.
        </p>
        <p>
          The Tide Tables provide predictions for Standard ports and Secondary Places as
          well as tools for calculating tide levels at anytime during the day.
          Additionally, it provides tables for Sun and Moon rise and set.
        </p>

        <h2 className="as-subheading">Tidal Definitions and Notes</h2>
        <p>
          Following are some of the most common definitions in the Queensland Tide Table
          publication. More definitions and notes available in the publication itself.
        </p>
        <ul>
          <li>
            <strong>LAT (lowest astronomical tide) and HAT (highest astronomical tide)</strong>
            {' '}&ndash; These are the lowest and highest levels which can be predicted to
            occur under average meteorological conditions and any combination of
            astronomical conditions.
          </li>
          <li>
            <strong>Datum of tidal heights</strong> &ndash; The height of the tide
            (expressed as metres and decimals) is referred to the port datum (LAT datum).
            When a low water falls below datum, it is marked with a minus sign (&minus;).
            When utilising a navigational chart, tidal height should be added to chart
            depth. If preceded by a minus sign, it should be subtracted.
          </li>
          <li>
            <strong>Secondary places</strong> &ndash; are those for which daily predictions
            are not provided in the Queensland Tide Tables. These locations are grouped and
            associated to the adjacent standard port with a similar tidal pattern. Data
            sufficient for calculating their times and heights is supplied following the
            standard port prediction tables.
          </li>
          <li>
            <strong>Tidal Levels</strong> &ndash; A list of tidal levels referred to
            Queensland Port Datum for standard ports and selected secondary places is given
            in Semidiurnal Tidal Planes and Diurnal Tidal Planes tables, which also provide
            the factors necessary to calculate tidal predictions at secondary places from
            the tidal predictions of the standard ports.
          </li>
          <li><strong>Rise</strong> &ndash; The rise of the tide is the height of the high water above port datum.</li>
          <li><strong>Range</strong> &ndash; The range of the tide is the difference between the height of high water and the next succeeding or last preceding low water.</li>
          <li>
            <strong>Semidiurnal tide</strong> &ndash; refers to a tide which has a period
            or cycle of approximately half of one tidal day (about 12.5 hours). Semidiurnal
            tides usually have two high and two low tides each day. The tides at Brisbane
            Bar are a typical example of semidiurnal tides.
          </li>
          <li>
            <strong>Diurnal tide</strong> &ndash; refers to a tide which has a period or
            cycle of approximately one tidal day (about 25 hours). Diurnal tides usually
            have one high and one low tide each day. The tides at Karumba are a typical
            example of diurnal tides.
          </li>
          <li>
            <strong>Meteorological effects on tides</strong> &ndash; Meteorological
            conditions which differ significantly from the seasonal averages, will cause
            corresponding differences between the predicted and the actual tide.
          </li>
        </ul>
        <p>
          Variations in tidal heights are mainly caused by strong or prolonged winds and by
          unusually high or low barometric pressure. Tidal predictions are computed for
          average barometric pressure. Low pressure systems tend to raise sea levels, and
          high pressure systems tend to lower them. However, the water does not adjust
          itself immediately to a change of pressure, but responds to the average change in
          pressure over a considerable area.
        </p>
        <p>
          The effect of wind on sea level, and therefore on tidal heights and times, is
          variable and depends on the topography of the area in question. In general, it
          can be said that wind will raise the sea level in the direction towards which it
          is blowing. A strong wind blowing straight onshore will cause the water to
          &ldquo;pile up&rdquo; resulting in high waters to be higher than predicted. Winds
          blowing off the land will have the reverse effect.
        </p>
        <p className="fw-semibold">
          Always check the latest marine weather forecast and add a safety margin when tide
          predictions are critical to passage planning.
        </p>

        <h2 className="as-subheading">Standard Ports, Secondary Ports and Under-keel Clearance</h2>
        <p>
          Work through each of the pages in this tides tutorial, starting with Standard
          Ports. Use the exercises at the bottom of each page to check your progress.
          Please use the Queensland 2025 Tide Table publication as a tide reference in all
          the exercises.
        </p>

        <div className="d-flex justify-content-end mt-4">
          <Link to="/tides-standard-ports" className="btn-as-primary">Next &ndash; Standard Ports</Link>
        </div>
      </div>
    </section>
  );
}
